import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import * as XLSX from 'xlsx';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Avatar,
  Chip,
  InputAdornment,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Assignment,
  School,
  InsertChart,
  CloudDownload,
  CheckCircle,
  Warning,
  Search,
  HelpOutline,
  BarChart
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: theme.shadows[4]
  }
}));

const ScoreChip = styled(Chip)(({ score, minscore, theme }) => ({
  backgroundColor: score <= minscore ? theme.palette.success.main : theme.palette.error.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  fontSize: '0.75rem'
}));

const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(4)
}));

const SubmissionTableContainer = styled(Paper)(({ theme }) => ({
  height: '600px',
  width: '100%',
  borderRadius: '12px',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
}));

const AnalyticsPage = () => {
  const theme = useTheme();
  const [selectedCollege, setSelectedCollege] = useState('');
  const [classrooms, setClassrooms] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedClassroom, setSelectedClassroom] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState('');
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [minAiScore, setMinAiScore] = useState(70);
  const [belowMinStudents, setBelowMinStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTeacherCollege();
  }, []);

  const fetchTeacherCollege = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const collegesSnapshot = await getDocs(collection(db, 'colleges'));
      for (const collegeDoc of collegesSnapshot.docs) {
        const teacherRef = doc(db, 'colleges', collegeDoc.id, 'teachers', user.uid);
        const teacherSnap = await getDoc(teacherRef);
        if (teacherSnap.exists()) {
          setSelectedCollege(collegeDoc.id);
          break;
        }
      }
    } catch (error) {
      console.error('Error fetching college:', error);
      setError('Failed to fetch college data');
    }
  };

  useEffect(() => {
    if (selectedCollege) {
      fetchClassrooms();
    }
  }, [selectedCollege]);

  const fetchClassrooms = async () => {
    try {
      const user = auth.currentUser;
      if (!user || !selectedCollege) return;

      const teacherRef = doc(db, 'colleges', selectedCollege, 'teachers', user.uid);
      const teacherSnap = await getDoc(teacherRef);

      if (!teacherSnap.exists()) {
        console.warn("Teacher doc not found.");
        return;
      }

      const assignedClassIds = teacherSnap.data().assignedClasses || [];

      const classDetails = await Promise.all(
        assignedClassIds.map(async (classId) => {
          const classRef = doc(db, 'colleges', selectedCollege, 'classes', classId);
          const classSnap = await getDoc(classRef);
          return classSnap.exists() ? { id: classId, ...classSnap.data() } : null;
        })
      );

      setClassrooms(classDetails.filter(Boolean));
    } catch (error) {
      console.error("Error fetching classrooms:", error);
      setError('Failed to fetch classrooms');
    }
  };

  useEffect(() => {
    if (selectedClassroom) {
      fetchAssignments(selectedClassroom);
    } else {
      setAssignments([]);
    }
  }, [selectedClassroom]);

  const fetchAssignments = async (classroomId) => {
    try {
      const assignmentsRef = collection(db, 'colleges', selectedCollege, 'classes', classroomId, 'assignments');
      const assignmentsSnapshot = await getDocs(assignmentsRef);
      setAssignments(assignmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error fetching assignments:", error);
      setError('Failed to fetch assignments');
    }
  };

  const handleFetchSubmissions = async () => {
    if (!selectedCollege || !selectedClassroom || !selectedAssignment) {
      setError("Please select a college, classroom, and assignment.");
      return;
    }

    setLoading(true);
    setSubmissions([]);
    setBelowMinStudents([]);
    setStats(null);
    setError(null);

    try {
      const submissionsRef = collection(
        db,
        "colleges",
        selectedCollege,
        "classes",
        selectedClassroom,
        "assignments",
        selectedAssignment,
        "submissions"
      );

      const submissionSnapshot = await getDocs(submissionsRef);
      const submissionData = await Promise.all(
        submissionSnapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const aiData = await fetchTokenData(data.link);
          return { id: docSnap.id, ...data, ...aiData };
        })
      );

      setSubmissions(submissionData);
      calculateStats(submissionData);
      filterBelowMinStudents(submissionData);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      setError('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (submissions) => {
    const validScores = submissions
      .map(s => parseFloat(s.aiScore))
      .filter(score => !isNaN(score));
    
    if (validScores.length === 0) return;

    const average = validScores.reduce((a, b) => a + b, 0) / validScores.length;
    console.log("Average AI Score:", average);
    const min = Math.min(...validScores);
    console.log("Minimum AI Score:", min);
    const max = Math.max(...validScores);
    console.log("Maximum AI Score:", max);
    const belowThreshold = validScores.filter(score => score < minAiScore).length;

    setStats({
      average: average.toFixed(2),
      min: min.toFixed(2),
      max: max.toFixed(2),
      total: submissions.length,
      belowThreshold,
      aboveThreshold: submissions.length - belowThreshold
    });
  };

  const fetchTokenData = async (url) => {
    if (!url) return { aiScore: 'N/A', aiText: 'N/A' };
    try {
      const [_, username, tokenId] = url.split('/').slice(3);
      const tokenDocRef = doc(db, 'users', username, 'tokens', tokenId);
      const tokenDoc = await getDoc(tokenDocRef);

      if (tokenDoc.exists()) {
        const tokenData = tokenDoc.data();
        return {
          aiScore: tokenData.accuracy || 'N/A',
          aiText: tokenData.text || 'N/A',
        };
      } else {
        console.error('No such document for AI evaluation!');
        return { aiScore: 'N/A', aiText: 'N/A' };
      }
    } catch (error) {
      console.error("Error fetching AI evaluation data:", error);
      return { aiScore: 'Error', aiText: 'Error' };
    }
  };

  const filterBelowMinStudents = (submissions) => {
    const filtered = submissions.filter(submission => {
      const score = parseFloat(submission.aiScore);
      return !isNaN(score) && score >= minAiScore;
    });
    setBelowMinStudents(filtered.map(submission => ({
      pid: submission.pid,
      name: submission.name,
      score: submission.aiScore
    })));
  };

  const exportToExcel = () => {
    setExcelLoading(true);
    const worksheetData = submissions.map(submission => [
      new Date(submission.timestamp).toLocaleString(),
      submission.name,
      submission.pid,
      submission.link,
      submission.aiText,
      submission.aiScore,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([
      ['Timestamp', 'Student Name', 'Student ID (PID)', 'Submission Link', 'AI Text', 'AI Score (%)'],
      ...worksheetData,
    ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Submissions');

    XLSX.writeFile(workbook, `Submissions_${selectedAssignment}.xlsx`);
    setExcelLoading(false);
  };

  // DataGrid columns //solve timestamp formatting issue
  const columns = [
    { field: 'timestamp', headerName: 'Timestamp', width: 180,
      valueFormatter: (params) => new Date(submissions.timestamp).toLocaleString()
    },
    { field: 'name', headerName: 'Student Name', width: 180 },
    { field: 'pid', headerName: 'Student ID', width: 150 },
    { field: 'link', headerName: 'Submission', width: 150,
      renderCell: (params) => (
        <Button 
          variant="outlined" 
          size="small" 
          href={params.value} 
          target="_blank"
          startIcon={<Assignment />}
          sx={{ textTransform: 'none' }}
        >
          View
        </Button>
      )
    },
    { field: 'aiText', headerName: 'AI Text', width: 250 },
    { field: 'aiScore', headerName: 'AI Score (%)', width: 150,
      renderCell: (params) => {
        const score = parseFloat(params.value);
        if (isNaN(score)) return params.value;
        
        return (
          <ScoreChip
            score={score}
            minscore={minAiScore}
            label={`${score.toFixed(2)}%`}
            avatar={<Avatar>{minAiScore >= score ? <CheckCircle /> : <Warning />}</Avatar>}
          />
        );
      }
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        backgroundColor: theme.palette.background.paper,
        p: 3,
        borderRadius: '12px',
        boxShadow: theme.shadows[1]
      }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            <InsertChart sx={{ verticalAlign: 'middle', mr: 1, color: theme.palette.primary.main }} />
            Submission Analytics
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Analyze student submissions and AI evaluation scores
          </Typography>
        </Box>
        {selectedCollege && (
          <Chip 
            label={`College: ${selectedCollege}`} 
            color="primary"
            sx={{ 
              fontSize: '0.875rem', 
              px: 2,
              py: 1.5,
              fontWeight: 500
            }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
          {error}
        </Alert>
      )}

      {/* Filters Section */}
      <FilterSection elevation={0}>
        <Typography variant="h6" component="h2" sx={{ 
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          color: theme.palette.text.primary
        }}>
          <Search sx={{ mr: 1, color: theme.palette.primary.main }} />
          Filter Submissions
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Students"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                sx: {
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: '8px'
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Minimum AI Score (%)"
              type="number"
              value={minAiScore}
              onChange={(e) => setMinAiScore(parseFloat(e.target.value))}
              InputProps={{
                sx: {
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: '8px'
                },
                inputProps: { min: 0, max: 100 },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Minimum acceptable AI score percentage">
                      <HelpOutline color="action" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={handleFetchSubmissions}
              disabled={!selectedCollege || !selectedClassroom || !selectedAssignment || loading}
              sx={{
                height: '56px',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600
              }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Loading...' : 'Fetch Submissions'}
            </Button>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Select Classroom"
              value={selectedClassroom}
              onChange={(e) => {
                setSelectedClassroom(e.target.value);
                setSelectedAssignment('');
                setSubmissions([]);
              }}
              variant="outlined"
              InputProps={{
                sx: {
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: '8px'
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <School color="action" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="" disabled>Select a classroom</MenuItem>
              {classrooms.map(classroom => (
                <MenuItem key={classroom.id} value={classroom.id}>
                  {classroom.name || classroom.id}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Select Assignment"
              value={selectedAssignment}
              onChange={(e) => setSelectedAssignment(e.target.value)}
              variant="outlined"
              disabled={!selectedClassroom}
              InputProps={{
                sx: {
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: '8px'
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <Assignment color="action" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="" disabled>Select an assignment</MenuItem>
              {assignments.map(assignment => (
                <MenuItem key={assignment.id} value={assignment.id}>
                  {assignment.title || assignment.id}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </FilterSection>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { 
              title: 'Average Score', 
              value: `${stats.average}%`, 
              color: 'primary',
              icon: <InsertChart />,
              progress: stats.average
            },
            { 
              title: 'Highest Score', 
              value: `${stats.max}%`, 
              color: 'success',
              icon: <CheckCircle />,
              progress: 100
            },
            { 
              title: 'Lowest Score', 
              value: `${stats.min}%`, 
              color: 'error',
              icon: <Warning />,
              progress: stats.min
            },
            { 
              title: 'Submissions', 
              value: stats.total, 
              color: 'info',
              icon: <BarChart />,
              meta: `${stats.aboveThreshold} Above / ${stats.belowThreshold} Below`
            }
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar sx={{ 
                      mr: 2,
                      backgroundColor: theme.palette[stat.color].light,
                      color: theme.palette[stat.color].dark
                    }}>
                      {stat.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {stat.title}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {stat.value}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {stat.progress ? (
                    <LinearProgress 
                      variant="determinate" 
                      value={stat.progress} 
                      color={stat.color}
                      sx={{ 
                        height: 6, 
                        borderRadius: 3, 
                        mt: 2,
                        backgroundColor: theme.palette[stat.color].light
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {stat.meta}
                    </Typography>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Results Section */}
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '300px',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '12px'
        }}>
          <CircularProgress size={60} />
        </Box>
      ) : submissions.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            backgroundColor: theme.palette.background.paper,
            p: 2,
            borderRadius: '12px'
          }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              Submission Results
            </Typography>
            <Button
              variant="contained"
              color="success"
              onClick={exportToExcel}
              disabled={excelLoading}
              startIcon={excelLoading ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {excelLoading ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </Box>

          <SubmissionTableContainer>
            <DataGrid
              rows={submissions}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              checkboxSelection
              disableSelectionOnClick
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              sx={{
                '& .MuiDataGrid-root': {
                    border: 'none'
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: `1px solid ${theme.palette.divider}`
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: theme.palette.background.default,
                  borderBottom: `1px solid ${theme.palette.divider}`
                },
                '& .MuiDataGrid-toolbarContainer': {
                  p: 2,
                  backgroundColor: 'transparent'
                }
              }}
            />
          </SubmissionTableContainer>

          {/* Below Threshold Students */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: '12px',
                backgroundColor: theme.palette.background.paper
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  color: theme.palette.error.main
                }}>
                  <Warning sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h3">
                    Students above Minimum Score ({minAiScore}%)
                  </Typography>
                </Box>
                {belowMinStudents.length > 0 ? (
                  <List dense sx={{ 
                    maxHeight: 300, 
                    overflow: 'auto',
                    backgroundColor: theme.palette.background.default,
                    borderRadius: '8px',
                    p: 1
                  }}>
                    {belowMinStudents.map((student, index) => (
                      <ListItem 
                        key={index} 
                        sx={{ 
                          mb: 1,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: '6px'
                        }}
                      >
                        <ListItemText
                          primary={student.name || 'Unknown'}
                          secondary={`PID: ${student.pid}`}
                          sx={{ flex: 1 }}
                        />
                        <ScoreChip
                          score={parseFloat(student.score)}
                          minscore={minAiScore}
                          label={`${student.score}%`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert 
                    severity="success" 
                    icon={<CheckCircle />}
                    sx={{
                      borderRadius: '8px'
                    }}
                  >
                    All students meet the minimum AI score requirement.
                  </Alert>
                )}
              </Paper>
            </Grid>
            
            {/* Score Distribution Placeholder */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: '12px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.background.paper
              }}>
                <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                  Score Distribution
                </Typography>
                <Box sx={{ 
                  flex: 1,
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  backgroundColor: theme.palette.background.default,
                  borderRadius: '8px'
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <InsertChart sx={{ 
                      fontSize: 60, 
                      color: theme.palette.text.disabled,
                      mb: 1
                    }} />
                    <Typography variant="body1" color="text.secondary">
                      Score distribution visualization
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      (Would show interactive chart here)
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      ) : selectedAssignment ? (
        <Paper sx={{ 
          p: 4, 
          mt: 4, 
          textAlign: 'center',
          borderRadius: '12px',
          backgroundColor: theme.palette.background.paper
        }}>
          <Typography variant="h6" color="text.secondary">
            No submissions found for this assignment.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your filters or check back later.
          </Typography>
        </Paper>
      ) : null}
    </Container>
  );
};

export default AnalyticsPage;